"use client"

import React, {useEffect, useState} from 'react';
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import {useForm} from "@mantine/form";
import {IArticleQueryInputForm, IArticleQueryOutputForm} from "@/form/article";
import {useDebouncedValue, useMediaQuery} from "@mantine/hooks";
import {indexArticleQuery} from "@/service";
import "dayjs/locale/zh-tw";
import {DatePickerInput, DatesProvider} from "@mantine/dates";
import cx from "clsx";
import {ActionIcon, Divider, Pagination, SimpleGrid, TextInput} from "@mantine/core";
import {IconLayoutGridFilled, IconListDetails, IconSearch} from "@tabler/icons-react";
import styles from "@/app/(index)/index.module.css";
import ArticleList from "@/app/(index)/news/list/[id]/article-list";
import ArticleListSM from "@/app/(index)/news/list/[id]/article-list-sm";
import ArticleCard from "@/app/(index)/news/list/[id]/article-card";
import {Rowdies} from "next/font/google";

const rowdies = Rowdies({
    weight: ['300', '400', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

export default function InterPage() {
    const { fontSize, lang } = useGlobalStateStore();
    const [value, setValue] = useState('');
    const [debounced] = useDebouncedValue(value, 500);
    const [outputResult, setOutputResult] = useState<IArticleQueryOutputForm | null>(null);
    const [viewType, setViewType] = useState<number>(1);
    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');

    const loadCurrentFontSize = (s: number, m: number, l: number) => {
        if(fontSize == 0) return `text-[${s}px]`;
        else if(fontSize == 1) return `text-[${m}px]`;
        else if(fontSize == 2) return `text-[${l}px]`;
    }

    const form = useForm<IArticleQueryInputForm>({
        mode: 'controlled',
        initialValues: {
            language: lang == "tw" ? "A" : "B",
            international: true,
            queryText: '',
            date: [null, null],
            rowsPerPage: 12,
            nowPage: 1
        }
    });

    useEffect(() => {
        form.setValues({
            nowPage: 1,
            rowsPerPage: form.getValues().rowsPerPage,
            queryText: debounced,
            language: lang == "tw" ? "A" : "B",
        });
        query();
    }, [form.getValues().rowsPerPage, form.getValues().date, debounced, lang]);

    const handleChangeNowPage = (n: number) => {
        form.setValues({ nowPage: n });
        query();
    }

    const query = () => {
        indexArticleQuery(form.getValues()).then(result => {
            setOutputResult(result);
        })
    }

    return (
        <DatesProvider settings={{ locale: 'zh-tw', firstDayOfWeek: 1, timezone: 'UTC'}}>
            <div>

                <form className={cx("flex justify-between", !mediaQueryLessSM && 'flex-col')}>
                    <div className="flex gap-[20px] items-center">
                        <TextInput leftSection={<IconSearch/>}
                                   value={value}
                                   placeholder="請輸入查詢字串..."
                                   onChange={(event) => setValue(event.currentTarget.value)}
                        />

                        <div className="w-[300px]">
                            <DatePickerInput
                                type="range"
                                placeholder="請選擇發佈日期起迄"
                                valueFormat="YYYY年MM月DD日"
                                clearable
                                key={form.key('date')}
                                {...form.getInputProps('date')}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-[20px]">
                        <div className="flex items-center">
                            <div className="text-[#333333]">每頁顯示</div>
                            <select className={styles.select}
                                    key={form.key('rowsPerPage')}
                                    {...form.getInputProps('rowsPerPage')}
                            >
                                <option value={12}>12</option>
                                <option value={24}>24</option>
                                <option value={48}>48</option>
                            </select>
                            <div className="text-[#333333]">筆</div>
                        </div>
                        <div>
                            <ActionIcon variant="white" aria-label="Settings" size="xl"
                                        onClick={() => setViewType(0)}>
                                <IconLayoutGridFilled size={36} stroke={1.5}
                                                      color={viewType == 0 ? "#1f5b61" : "#b6cfce"}/>
                            </ActionIcon>

                            <ActionIcon variant="white" aria-label="Settings" size="xl"
                                        onClick={() => setViewType(1)}>
                                <IconListDetails size={36} stroke={2}
                                                 color={viewType == 1 ? "#1f5b61" : "#b6cfce"}/>
                            </ActionIcon>
                        </div>
                    </div>
                </form>

                <Divider color="#cedbdc" my={30}/>


                {
                    viewType == 1 && (
                        <div className="flex flex-col gap-3">
                            {
                                outputResult?.obj?.map((item, index) =>
                                    mediaQueryLessSM ? (
                                        <ArticleList item={item} key={`listview-${index}`}/>
                                    ) : (
                                        <ArticleListSM item={item} key={`listview-sm-${index}`}/>
                                    )
                                )
                            }
                        </div>
                    )
                }
                {
                    viewType == 0 && (
                        <SimpleGrid cols={mediaQueryLessSM ? 3 : 1} spacing={40}>
                            {
                                outputResult?.obj?.map((item, index) => (
                                    <ArticleCard item={item} key={`cardview-${index}`}/>
                                ))
                            }
                        </SimpleGrid>
                    )
                }

                {
                    outputResult?.totalRows == 0 && (
                        <div className="text-3xl font-bold">查無資料，請重新查詢</div>
                    )
                }

                {
                    (outputResult?.totalPages ?? 0) > 1 && (
                        <div className="mt-10 flex justify-end">
                            <Pagination total={outputResult?.totalPages!!}
                                        value={outputResult?.nowPage}
                                        radius={"xl"}
                                        color="#316d68"
                                        siblings={2}
                                        className={cx(rowdies.className, "font-light")}
                                        onChange={handleChangeNowPage} mt="sm"/>
                        </div>
                    )
                }

            </div>
        </DatesProvider>
    );
}
